import React from "react";
import ReactDOM from 'react-dom/client';
import App from "./App";
import * as serviceWorker from "./serviceWorker";
import { BrowserRouter } from "react-router-dom";
import "./i18n";
import { Provider } from "react-redux";

import store from "./store";
import {
  FONT_SCALE_STORAGE_KEY,
  FONT_FAMILY_STORAGE_KEY,
  FONT_COLOR_STORAGE_KEY,
  UI_THEME_STORAGE_KEY,
  fontScaleTypes,
  fontFamilyTypes,
  fontColorTypes,
  uiThemeTypes,
} from "./constants/layout";
import { FlagProvider } from "libs/context/FlagContext";
import { UserProvider } from "libs/context/UserContext";
import { PermissionProvider } from "libs/context/PermissionContext";
import { NotificationProvider } from "libs/context/NotificationContext";
import { JobCategoryProvider } from "libs/context/JobCategoryContext";
import { AIFadvPackageProvider } from "libs/context/AIFadvPackageContext";
import { ClientPlanProvider } from "libs/context/ClientPlanContext";
import { CallExecutiveProvider } from "libs/context/CallExecutiveContext";
import { JobRequiredProvider } from "libs/context/JobRequiredContext";
import { UserIPSettingsProvider } from "libs/context/UserIPSettingsContext";
import { MailTemplateProvider } from "libs/context/MailTemplateContext";
import { ConversationTemplateProvider } from "libs/context/ConversationTemplateContext";
import { AISmsTemplateProvider } from "libs/context/AISmsTemplateContext";
import { AIAddonCallTemplateProvider } from "libs/context/AIAddonCallTemplateContext";
import { AIScheduleMatrixProvider } from "libs/context/AIScheduleMatrixContext";
import { AIScheduleMatrixGroupProvider } from "libs/context/AIScheduleMatrixGroupContext";
import { NotificationSettingsProvider } from "libs/context/NotificationSettingsContext";
import { VerifyFadvCsvProvider } from "libs/context/VerifyFadvCsvContext";
import { BlockFadvRequestorProvider } from "libs/context/BlockFadvRequestorContext";
import { DocRequiredProvider } from "libs/context/DocRequiredContext";
import { OnboardingDocumentProvider } from "libs/context/OnboardingDocumentContext";
import { JobProvider } from "libs/context/JobContext";
import { BadFitResolutionReasonProvider } from "libs/context/BadFitResolutionReasonContext";
import { QuestionTypeProvider } from "libs/context/QuestionTypeContext";
import { AuditingParameterProvider } from "libs/context/AuditingParameterContext";
import { SourceProvider } from "libs/context/SourceContext";
import { ReasonProvider } from "libs/context/ReasonContext";
import { TaskBucketProvider } from "libs/context/TaskBucketContext";
import { AiRecruiterLogProvider } from "libs/context/AiRecruiterLogContext";
import { CallScheduledReportProvider } from "libs/context/CallScheduledReportContext";
import { TwilioPhoneProvider } from "libs/context/TwilioPhoneContext";
import { CompanyProvider } from "libs/context/CompanyContext";
import { FindCompanyUserProvider } from "libs/context/FindCompanyUserContext";
import { ClientRegisterProvider } from "libs/context/ClientRegisterContext";
import { ClientReviewProvider } from "libs/context/ClientReviewContext";
import { JobDescriptionProvider } from "libs/context/JobDescriptionContext";
import { ClientSourceProvider } from "libs/context/ClientSourceContext";
import { ClientPreQuestionProvider } from "libs/context/ClientPreQuestionContext";
import { ClientSectionProvider } from "libs/context/ClientSectionContext";
import { OffboardingProvider } from "libs/context/OffboardingContext";
import { OnboardingProvider } from "libs/context/OnboardingContext";
import { ClientAppreciationProvider } from "libs/context/ClientAppreciationContext";
import { CommonFilterOptionsProvider } from "libs/context/CommonFilterOptionsContext";
import { TableColumnPreferencesProvider } from "libs/context/TableColumnPreferencesContext";
import { TablePageSizePreferencesProvider } from "libs/context/TablePageSizePreferencesContext";
import { FilterPreferencesProvider } from "libs/context/FilterPreferencesContext";
import { StatusMetadataProvider } from "libs/context/StatusMetadataContext";
import { HeaderSettingsSyncProvider } from "libs/context/HeaderSettingsSyncContext";

/* Apply saved font/theme before first paint to avoid a flash of defaults */
(() => {
  try {
    const fontScale = localStorage.getItem(FONT_SCALE_STORAGE_KEY);
    const fontFamily = localStorage.getItem(FONT_FAMILY_STORAGE_KEY);
    const fontColor = localStorage.getItem(FONT_COLOR_STORAGE_KEY);
    const uiTheme = localStorage.getItem(UI_THEME_STORAGE_KEY);
    document.documentElement.setAttribute(
      "data-font-scale",
      fontScale === fontScaleTypes.SMALL ||
        fontScale === fontScaleTypes.CURRENT ||
        fontScale === fontScaleTypes.LARGE
        ? fontScale
        : fontScaleTypes.CURRENT
    );
    document.documentElement.setAttribute(
      "data-font-family",
      fontFamily === fontFamilyTypes.ROBOTO ||
        fontFamily === fontFamilyTypes.OPEN_SANS ||
        fontFamily === fontFamilyTypes.POPPINS
        ? fontFamily
        : fontFamilyTypes.POPPINS
    );
    document.documentElement.setAttribute(
      "data-font-color",
      fontColor === fontColorTypes.CHARCOAL ||
        fontColor === fontColorTypes.SOFT_NAVY ||
        fontColor === fontColorTypes.RED ||
        fontColor === fontColorTypes.PRIMARY ||
        fontColor === fontColorTypes.DEEP_SAPPHIRE ||
        fontColor === fontColorTypes.DEEP_MAROON ||
        fontColor === fontColorTypes.DEEP_BLACK ||
        fontColor === fontColorTypes.CURRENT
        ? fontColor
        : fontColorTypes.CURRENT
    );
    document.documentElement.setAttribute(
      "data-ui-theme",
      uiTheme === uiThemeTypes.MODERN || uiTheme === uiThemeTypes.DARK
        ? uiTheme
        : uiThemeTypes.CURRENT
    );
  } catch (_error) {
    document.documentElement.setAttribute("data-font-scale", fontScaleTypes.CURRENT);
    document.documentElement.setAttribute("data-font-family", fontFamilyTypes.POPPINS);
    document.documentElement.setAttribute("data-font-color", fontColorTypes.CURRENT);
    document.documentElement.setAttribute("data-ui-theme", uiThemeTypes.CURRENT);
  }
})();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <Provider store={store}>
    <React.Fragment>
      {/* <BrowserRouter basename={`${process.env.REACT_APP_BASENAME}`}> */}
      <BrowserRouter>
        <HeaderSettingsSyncProvider>
          <CommonFilterOptionsProvider>
            <StatusMetadataProvider>
            <TableColumnPreferencesProvider>
            <TablePageSizePreferencesProvider>
            <FilterPreferencesProvider>
            <FlagProvider>
              <UserProvider>
                <NotificationProvider>
                <PermissionProvider>
                <JobCategoryProvider>
                <AIFadvPackageProvider>
                <ClientPlanProvider>
                  <CallExecutiveProvider>
                  <JobRequiredProvider>
                    <UserIPSettingsProvider>
                    <MailTemplateProvider>
                    <ConversationTemplateProvider>
                    <AISmsTemplateProvider>
                    <AIAddonCallTemplateProvider>
                    <AIScheduleMatrixProvider>
                    <AIScheduleMatrixGroupProvider>
                    <NotificationSettingsProvider>
                    <VerifyFadvCsvProvider>
                    <BlockFadvRequestorProvider>
                    <DocRequiredProvider>
                      <OnboardingDocumentProvider>
                      <JobProvider>
                        <BadFitResolutionReasonProvider>
                        <QuestionTypeProvider>
                          <AuditingParameterProvider>
                            <SourceProvider>
                              <ReasonProvider>
                                <TaskBucketProvider>
                                  <AiRecruiterLogProvider>
                                    <CallScheduledReportProvider>
                                    <TwilioPhoneProvider>
                                      <CompanyProvider>
                                        <FindCompanyUserProvider>
                                          <ClientRegisterProvider>
                                            <ClientReviewProvider>
                                              <JobDescriptionProvider>
                                              <ClientSourceProvider>
                                                <ClientPreQuestionProvider>
                                                  <ClientSectionProvider>
                                                    <ClientAppreciationProvider>
                                                      <OffboardingProvider>
                                                        <OnboardingProvider>
                                                          <App />
                                                        </OnboardingProvider>
                                                      </OffboardingProvider>
                                                    </ClientAppreciationProvider>
                                                  </ClientSectionProvider>
                                                </ClientPreQuestionProvider>
                                              </ClientSourceProvider>
                                              </JobDescriptionProvider>
                                            </ClientReviewProvider>
                                          </ClientRegisterProvider>
                                        </FindCompanyUserProvider>
                                      </CompanyProvider>
                                    </TwilioPhoneProvider>
                                    </CallScheduledReportProvider>
                                  </AiRecruiterLogProvider>
                                </TaskBucketProvider>
                              </ReasonProvider>
                            </SourceProvider>
                          </AuditingParameterProvider>
                        </QuestionTypeProvider>
                        </BadFitResolutionReasonProvider>
                      </JobProvider>
                      </OnboardingDocumentProvider>
                    </DocRequiredProvider>
                    </BlockFadvRequestorProvider>
                    </VerifyFadvCsvProvider>
                    </NotificationSettingsProvider>
                    </AIScheduleMatrixGroupProvider>
                    </AIScheduleMatrixProvider>
                    </AIAddonCallTemplateProvider>
                    </AISmsTemplateProvider>
                    </ConversationTemplateProvider>
                    </MailTemplateProvider>
                    </UserIPSettingsProvider>
                  </JobRequiredProvider>
                </CallExecutiveProvider>
                </ClientPlanProvider>
                </AIFadvPackageProvider>
                </JobCategoryProvider>
                </PermissionProvider>
                </NotificationProvider>
              </UserProvider>
            </FlagProvider>
            </FilterPreferencesProvider>
            </TablePageSizePreferencesProvider>
            </TableColumnPreferencesProvider>
            </StatusMetadataProvider>
          </CommonFilterOptionsProvider>
        </HeaderSettingsSyncProvider>
      </BrowserRouter>
    </React.Fragment>
  </Provider>
);

serviceWorker.unregister()
